import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { createListFolderMutation } from 'api/mutations';
import { AncestorIdData, AncestorNameData } from 'modules/domains';
import { useTranslation } from 'modules/domains/list';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { NamedFolder, ResponseWrapper } from 'ui/toast';
import { FolderNameLink } from 'ui/indexPages/folders/Folders.styles';
import { LIST_QUERY_PARAMS } from 'modules/listIndex/types';

export const useCreateListFolder = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [createFolder] = useMutation(createListFolderMutation);

  return useCallback(
    async (newFolderName: string, newFolderDestination: AncestorIdData & AncestorNameData) => {
      try {
        const { data: creationMutationData, errors: creationMutationErrors } = await createFolder({
          variables: {
            name: newFolderName,
            locationId: newFolderDestination.id,
          },
        });

        const destinationFolderName =
          newFolderDestination.name === '__root__' ? 'Main' : newFolderDestination.name;

        if (!creationMutationData?.createListFolder || creationMutationErrors) {
          dispatch(
            addNegativeToastWithErrorId(
              t('indexPage.toasts.createFolder.error.default'),
              'createListFolder',
            ),
          );
          return;
        }
        const newFolderId = creationMutationData.createListFolder;
        const { location } = window;
        const urlParams = new URLSearchParams(location.search);
        urlParams.set(LIST_QUERY_PARAMS.folderId, newFolderId.toString());

        // Should update to apply translations to this toast when modal is created
        dispatch(
          addPositiveToast(
            <ResponseWrapper>
              <FolderNameLink to={`${location.pathname}?${urlParams.toString()}`}>
                <NamedFolder name={newFolderName} />
              </FolderNameLink>{' '}
              was successfully created
              {destinationFolderName ? (
                <span>
                  {' '}
                  in the <NamedFolder name={destinationFolderName} /> folder.
                </span>
              ) : (
                '.'
              )}
            </ResponseWrapper>,
          ),
        );
      } catch (error) {
        dispatch(
          addNegativeToastWithErrorId(
            t('indexPage.toasts.createFolder.error.default'),
            'useCreateListFolder',
            error,
          ),
        );
      }
    },
    [createFolder, dispatch],
  );
};
